import { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, viewportOnce } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EJS_PUBLIC = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function EarlyAccessCTA() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, { email, role: "general" });
      if (EJS_SERVICE && EJS_TEMPLATE && EJS_PUBLIC) {
        try {
          await emailjs.send(
            EJS_SERVICE,
            EJS_TEMPLATE,
            { name: "there", email, company: "-", role: "Early Access" },
            { publicKey: EJS_PUBLIC }
          );
        } catch (err) {
          console.warn("EmailJS send skipped:", err?.text || err);
        }
      }
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white py-16 sm:py-20" data-testid="cta-section">
      <div className="mx-auto max-w-5xl px-2.5 sm:px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid-lines relative overflow-hidden rounded-[28px] border border-[#e5e7eb] bg-[#f5f7fb] px-4 py-10 text-center sm:px-12 sm:py-16"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#3b82f6]/15 blur-[100px]" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#1e3a8a]/10 blur-[100px]" />

          <div className="relative">
            <p className="eyebrow text-[#3b82f6]">Be among the first</p>
            <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-extrabold leading-[1.06] tracking-tight text-[#111827] sm:text-4xl">
              LintelGrid is currently under development.
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-[#475569] sm:text-base">
              Join our early access community to receive product updates, get
              priority access, and help shape what we're building.
            </p>

            {!done ? (
              <form
                onSubmit={submit}
                className="mx-auto mt-8 flex w-full max-w-md flex-col gap-3 sm:flex-row"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full rounded-full border border-[#e5e7eb] bg-white px-4 py-2.5 text-[13px] text-[#111827] outline-none transition-all placeholder:text-[#94a3b8] focus:border-[#3b82f6] focus:ring-4 focus:ring-[#3b82f6]/10"
                  data-testid="cta-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary group flex shrink-0 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white transition-all disabled:opacity-70"
                  data-testid="cta-submit-button"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Join Early Access
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-auto mt-8 flex max-w-md items-center justify-center gap-3 rounded-full border border-[#3b82f6]/20 bg-white px-5 py-3"
                data-testid="cta-success"
              >
                <CheckCircle2 className="h-5 w-5 text-[#1e3a8a]" />
                <span className="text-sm font-semibold text-[#111827]">
                  You're on the list — we'll be in touch soon.
                </span>
              </motion.div>
            )}

            <p className="mt-4 text-xs text-[#94a3b8]">
              No spam. Just meaningful updates as we build.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
